import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import type { AdminApi, SegmentSizePoint } from '../../api/client.ts';
import { formatCount } from '../audience/ChannelReachBreakdown.tsx';

const W = 600;
const H = 160;
const PAD = 24;
const DAY = 86_400_000;

/** Size points on a day axis: estimates draw their interval as a band behind the line. */
function Chart({ points, days, today }: { points: SegmentSizePoint[]; days: number; today: string }) {
  const end = Date.parse(`${today}T00:00:00Z`);
  const start = end - (days - 1) * DAY;
  const max = Math.max(1, ...points.map((p) => p.high));
  const x = (day: string) => PAD + ((Date.parse(`${day}T00:00:00Z`) - start) / Math.max(DAY, end - start)) * (W - 2 * PAD);
  const y = (v: number) => H - PAD - (v / max) * (H - 2 * PAD);
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.day).toFixed(1)},${y(p.count).toFixed(1)}`).join(' ');
  const band = points.length > 1 ? `${points.map((p, i) => `${i ? 'L' : 'M'}${x(p.day).toFixed(1)},${y(p.high).toFixed(1)}`).join(' ')} ${[...points].reverse().map((p) => `L${x(p.day).toFixed(1)},${y(p.low).toFixed(1)}`).join(' ')} Z` : '';
  const last = points.at(-1)!;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Size over the last ${days} days: from ${formatCount(points[0]!.count)} to ${formatCount(last.count)}`} className="segment-history__chart">
      {band && <path d={band} className="segment-history__band" fill="currentColor" opacity={0.12} />}
      <path d={line} fill="none" stroke="currentColor" strokeWidth={2} />
      {points.map((p) => (
        <circle key={p.day} cx={x(p.day)} cy={y(p.count)} r={3} fill="currentColor">
          <title>{`${p.day}: ${formatCount(p.count)}${p.mode === 'estimate' ? ` (${formatCount(p.low)}–${formatCount(p.high)})` : ''}`}</title>
        </circle>
      ))}
      <text x={PAD} y={H - 6} fontSize={11}>
        {new Date(start).toISOString().slice(0, 10)}
      </text>
      <text x={W - PAD} y={H - 6} fontSize={11} textAnchor="end">
        {today}
      </text>
    </svg>
  );
}

/**
 * Size of a segment over 30 or 90 days (BB-48), from the daily snapshots: exact points for small environments,
 * estimates with their 95 % interval as a band.
 */
export function SegmentHistoryChart({ environmentId, segmentId, today = new Date().toISOString().slice(0, 10) }: { environmentId: string; segmentId: string; today?: string }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [days, setDays] = useState<30 | 90>(30);
  const history = useQuery({ queryKey: ['segment-history', environmentId, segmentId, days], queryFn: () => api.segmentHistory(environmentId, segmentId, days) });
  return (
    <section aria-label="Size history" className="segment-history">
      <h3>Size history</h3>
      <div role="radiogroup" aria-label="Period">
        {([30, 90] as const).map((d) => (
          <label key={d}>
            <input type="radio" name="segment-history-days" checked={days === d} onChange={() => setDays(d)} /> {d} days
          </label>
        ))}
      </div>
      {history.isPending ? (
        <p aria-busy="true">Loading…</p>
      ) : history.isError ? (
        <p role="alert">The history could not be loaded.</p>
      ) : history.data.items.length === 0 ? (
        <p className="hint">No daily sizes yet: they are recorded every night after 03:00 in the environment's time zone.</p>
      ) : (
        <Chart points={history.data.items} days={days} today={today} />
      )}
    </section>
  );
}
